import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import type { ReactNode } from "react";

type AgeSortDirection = "asc" | "desc" | null;

const HEADER_CELL_CLASS =
  "px-4 py-2.5 text-left text-[12px] font-semibold text-muted-foreground whitespace-nowrap";

function QueueHeaderCell({ children }: Readonly<{ children: ReactNode }>) {
  return <th className={HEADER_CELL_CLASS}>{children}</th>;
}

function AgeSortHeader({
  direction,
  onToggle,
}: Readonly<{
  direction: AgeSortDirection;
  onToggle: () => void;
}>) {
  return (
    <QueueHeaderCell>
      <button
        type="button"
        onClick={onToggle}
        className="inline-flex items-center gap-1 transition-colors hover:text-foreground"
        title={
          direction === "desc"
            ? "Sorted highest age first — click to sort lowest first"
            : direction === "asc"
            ? "Sorted lowest age first — click to remove sorting"
            : "Not sorted — click to sort highest age first"
        }
      >
        Age
        {direction === "desc" ? (
          <ChevronDown className="size-3.5" />
        ) : direction === "asc" ? (
          <ChevronUp className="size-3.5" />
        ) : (
          <ChevronsUpDown className="size-3.5 opacity-50" />
        )}
      </button>
    </QueueHeaderCell>
  );
}

interface UnderwritingQueueAccordionProps {
  count: number;
  ageSortDirection: AgeSortDirection;
  onToggleAgeSort: () => void;
  children: ReactNode;
}

export function UnderwritingQueueAccordion({
  ageSortDirection,
  onToggleAgeSort,
  children,
}: Readonly<UnderwritingQueueAccordionProps>) {
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm">
      <div className="border-b border-border px-4 py-3">
        <h2 className="text-lg font-semibold text-foreground">Applications</h2>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="border-b border-border">
              <QueueHeaderCell>Application ID</QueueHeaderCell>
              <QueueHeaderCell>Customer Name</QueueHeaderCell>
              <QueueHeaderCell>Affiliate</QueueHeaderCell>
              <AgeSortHeader direction={ageSortDirection} onToggle={onToggleAgeSort} />
              <QueueHeaderCell>Claimed by</QueueHeaderCell>
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}
